
import { motion } from "framer-motion";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle01Icon, Shield01Icon, File01Icon, ZapIcon } from "@hugeicons/core-free-icons";
import { AnimatedText } from "../ui/animated-text";
import { ShinyButton } from "../ui/shiny-button";
import { VideoPlayer } from "../ui/video-player";

export function TaxiFusionCTA() {
    return (
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
            <div className="container mx-auto max-w-4xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="space-y-12 text-left sm:text-center"
                >

                    {/* Copy & Action */}
                    <div className="space-y-16">
                        <div>
                            <AnimatedText
                                lines={["Die komplette TSE-Lösung:", "Hardware + Software"]}
                                className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 tracking-tight"
                                direction="up"
                                as="h2"
                            />

                            <p className="text-base sm:text-lg text-gray-400 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto px-2 text-balance">
                                Dieses TSE-zertifizierte Gerät ist der erste Schritt zur vollständigen Compliance. Für die komplette Lösung kombinieren Sie es mit <strong className="text-white">TaxiFusion</strong> – der führenden Software für Taxi- und Mietwagenunternehmen.
                            </p>
                            {/* Video Player */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                                viewport={{ once: true }}
                                className="relative max-w-4xl mx-auto"
                            >
                                <VideoPlayer
                                    src="https://taxifusion.de/product/tf-demo.mp4"
                                    className="w-full"
                                    autoplay={true}
                                    loop={true}
                                    muted={true}
                                />
                            </motion.div>

                        </div>

                        {/* Value Stack */}
                        <div className="mb-8">
                            <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-white text-balance">Was Sie mit TaxiFusion erhalten:</h3>
                            <ul className="space-y-3 sm:space-y-4 max-w-2xl mx-auto">
                                <li className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors text-left">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={20} className="text-green-400/70" strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1 text-balance">TSE-Compliance garantiert</h4>
                                        <p className="text-sm text-gray-400 text-balance">Automatische, manipulationssichere Aufzeichnung aller Fahrten gemäß KassenSichV</p>
                                    </div>
                                </li>

                                <li className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors text-left">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <HugeiconsIcon icon={File01Icon} size={20} className="text-blue-400/70" strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1 text-balance">Kassenmeldung ans Finanzamt – kostenlos</h4>
                                        <p className="text-sm text-gray-400 text-balance">Automatische Übermittlung der TSE-Daten an das Finanzamt, ohne zusätzliche Gebühren</p>
                                    </div>
                                </li>

                                <li className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors text-left">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <HugeiconsIcon icon={ZapIcon} size={20} className="text-yellow-400/70" strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1 text-balance">Einfach zu bedienen</h4>
                                        <p className="text-sm text-gray-400 text-balance">Intuitive Software, die Ihre tägliche Arbeit erleichtert statt komplizierter zu machen</p>
                                    </div>
                                </li>

                                <li className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors text-left">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <HugeiconsIcon icon={Shield01Icon} size={20} className="text-purple-400/70" strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1 text-balance">Komplette Lösung</h4>
                                        <p className="text-sm text-gray-400 text-balance">Von der Hardware-Compliance bis zur Software-Compliance – alles aus einer Hand</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        {/* Why TaxiFusion */}
                        <div className="mb-8 p-4 sm:p-6 rounded-xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 max-w-2xl mx-auto">
                            <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3 text-white text-balance">Warum TaxiFusion?</h3>
                            <p className="text-sm text-gray-400 leading-relaxed text-balance">
                                TaxiFusion wurde speziell für Taxi- und Mietwagenunternehmen entwickelt und erfüllt alle gesetzlichen Anforderungen. Die Software arbeitet nahtlos mit der TBOX zusammen und sorgt dafür, dass Sie rechtssicher unterwegs sind – ohne bürokratischen Aufwand.
                            </p>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-start sm:justify-center">
                            <a href="https://taxifusion.de" target="_blank" rel="noopener noreferrer" className="bg-white text-black w-full sm:w-auto px-10 py-5 border border-white/20 rounded-full font-bold text-lg hover:bg-white/90 transition-all backdrop-blur-sm text-center">
                                Mehr über TaxiFusion erfahren
                            </a>
                        </div>

                        {/* Trust Seal */}
                        {/* <div className="pt-4 border-t border-white/10">
                            <p className="text-sm text-gray-400 text-center">
                                <strong className="text-white">Von über 500 Taxi- und Mietwagenunternehmen vertraut.</strong> Zertifiziert und BSI-konform.
                            </p>
                        </div> */}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

